import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";

const S3_URL = process.env.REACT_APP_S3_URL;

export default function SalesReport({ data, companies }) {
  const companyMap = companies.reduce((acc, company) => {
    acc[company.id] = company.name;
    return acc;
  }, {});

  // Agrupa por company_id e por seller id somando vendas e metas (por id da meta)
  const groupedByCompany = {};

  data.forEach((entry) => {
    entry.sellers.forEach((seller) => {
      const { company_id, id: sellerId } = seller;

      if (!groupedByCompany[company_id]) {
        groupedByCompany[company_id] = {};
      }

      if (!groupedByCompany[company_id][sellerId]) {
        groupedByCompany[company_id][sellerId] = {
          ...seller,
          sales: [...seller.sales],
          goals: [...seller.goals],
        };
      } else {
        // acumula vendas
        groupedByCompany[company_id][sellerId].sales.push(...seller.sales);

        // acumula metas por id da meta
        const existingGoalsMap = {};
        groupedByCompany[company_id][sellerId].goals.forEach((goal) => {
          if (goal.id) existingGoalsMap[goal.id] = { ...goal };
        });

        seller.goals.forEach((goal) => {
          if (!goal.id) return;

          if (
            existingGoalsMap[goal.id] &&
            existingGoalsMap[goal.id].target != null &&
            goal.target != null
          ) {
            existingGoalsMap[goal.id].target += goal.target;
          } else if (!existingGoalsMap[goal.id]) {
            existingGoalsMap[goal.id] = { ...goal };
          }
        });

        groupedByCompany[company_id][sellerId].goals = Object.values(
          existingGoalsMap
        );
      }
    });
  });

  // Função para somar vendas totais (considerando quantity * value)
  const sumSalesValue = (sales) =>
    sales.reduce((acc, s) => acc + s.value, 0);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  return (
    <Card sx={{ p: 2, display: "flex", flexDirection: "column" }}>
      <CardHeader title="Metas" />
      {Object.entries(groupedByCompany)
        .map(([companyId, sellersObj]) => {
          const sellers = Object.values(sellersObj).map((seller) => ({
            ...seller,
            totalSales: sumSalesValue(seller.sales),
            salesCount: seller.sales.length,
          }));

          // Ordena os vendedores da empresa
          sellers.sort((a, b) => b.totalSales - a.totalSales);

          const goalTotals = {};
          const totalCompanySales = sellers.reduce((acc, seller) => {
            const sellerSalesTotal = seller.totalSales;
            seller.goals.forEach((goal) => {
              if (!goal.name) return;

              if (!goalTotals[goal.name]) {
                goalTotals[goal.name] = { target: 0, sold: 0 };
              }

              goalTotals[goal.name].target += goal.target || 0;
              goalTotals[goal.name].sold += sellerSalesTotal;
            });

            return acc + sellerSalesTotal;
          }, 0);

          return {
            companyId,
            companyName: companyMap[companyId] || `Empresa #${companyId}`,
            sellers,
            goalTotals,
            totalCompanySales,
          };
        })
        .sort((a, b) => b.totalCompanySales - a.totalCompanySales) // empresas com maior venda primeiro
        .map(({ companyId, companyName, sellers, goalTotals }) => (
          <Card key={companyId} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 0 }}>
                {companyName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>
                  {sellers.reduce((acc, s) => acc + s.salesCount, 0)}
                </strong>{" "}
                vendas
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                <strong>
                  {formatCurrency(sellers.reduce((acc, s) => acc + s.totalSales, 0).toFixed(2))}
                </strong>
              </Typography>


              {Object.entries(goalTotals).map(([goalName, { sold, target }]) => {
                const progress = Math.min((sold / target) * 100, 100);
                const isReached = sold >= target;

                return (
                  <Box key={goalName} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        {goalName}
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(target)} <span sx={{ color: 'text.secondary', mb: 2 }}>({formatCurrency(sold-target)})</span>
                      </Typography>
                    </Box>
                    <Box mt={1} position="relative">
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 18,
                          borderRadius: 5,
                          border: `1px solid ${
                            isReached ? green[200] : red[200]
                          }`,
                          backgroundColor: "#eee",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: isReached ? green[500] : red[400],
                          },
                        }}
                      />
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="textSecondary"
                        >
                          {progress.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}

              <Grid container spacing={2} sx={{ mt: 2 }}>
                {sellers.map((seller) => {
                  if (!seller.goals || seller.goals.length === 0) return null;

                  return (
                    <Grid item xs={12} md={6} key={seller.id}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #ddd",
                          borderRadius: 2,
                          backgroundColor: "#f9f9f9",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Avatar
                            src={`${S3_URL}/img/user/${seller.avatar}`}
                          />
                          <Box>
                            <div
                              key={seller.id}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {seller.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                <strong>{seller.salesCount}</strong> vendas 
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                <strong>{formatCurrency(seller.totalSales.toFixed(2))}</strong>
                              </Typography>
                            </div>
                          </Box>

                        </Box>

                        {seller.goals.map((goal) => {
                          if (!goal.target) return null;
                          const progress = Math.min(
                            (seller.totalSales / goal.target) * 100,
                            100
                          );
                          const isReached = seller.totalSales >= goal.target;

                          return (
                            <Box key={goal.id || goal.name} sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <Typography variant="body2">
                                  {goal.name}
                                </Typography>
                                <Typography variant="body2">
                                  {formatCurrency(goal.target)}
                                </Typography>
                              </Box>
                              <Box mt={0} position="relative">
                                <LinearProgress
                                  variant="determinate"
                                  value={progress}
                                  sx={{
                                    height: 15,
                                    borderRadius: 5,
                                    border: `1px solid ${
                                      isReached ? green[200] : red[200]
                                    }`,
                                    backgroundColor: "#eee",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: isReached
                                        ? green[500]
                                        : red[400],
                                    },
                                  }}
                                />
                                <Box
                                  position="absolute"
                                  top={0}
                                  left={0}
                                  right={0}
                                  bottom={0}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    color="textSecondary"
                                  >
                                    {progress.toFixed(1)}%
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        ))}
    </Card>
  );
}
